import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Chip, Stack } from "@mui/material";
import { useUpdateHunterPrerequisitesMutation } from "Api/Mutations/Hunter/useUpdateHunterPrerequisitesMutation";
import { PrerequisitesStatusHeader } from "Components/Hunter/HunterPrerequisitesForm/PrerequisitesStatusHeader";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import {
  type FormValues,
  hunterPrerequisitesFormSchema,
} from "Components/Hunter/HunterPrerequisitesForm/schema";
import {
  type HunterPrerequisiteItemDto,
  PrerequisiteCompletionStatus,
} from "Infrastructure/Api/Api";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.HunterPrerequisites;

type Props = {
  userID: number;
  prerequisites: Array<HunterPrerequisiteItemDto>;
  completedCount: number;
  totalCount: number;
};

export const HunterPrerequisitesForm = ({
  userID,
  prerequisites,
  completedCount,
  totalCount,
}: Props) => {
  const { t } = useResource();

  const defaultItems = prerequisites.map(
    ({ prerequisiteType, completionStatus, note }) => ({
      prerequisiteType,
      isCompleted:
        completionStatus !== PrerequisiteCompletionStatus.NotCompleted,
      note,
    }),
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(hunterPrerequisitesFormSchema),
    defaultValues: { items: defaultItems },
  });

  const { mutate: updatePrerequisites, isPending } =
    useUpdateHunterPrerequisitesMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.SaveSuccess));
      },
      onError: () => {
        toast.error(t(RESOURCES.SaveError));
      },
    });

  const onSubmit = ({ items }: FormValues) =>
    updatePrerequisites(
      { userID, items },
      { onSuccess: () => reset({ items }) },
    );

  const items = watch("items");

  const autoCompletedIndices = new Set(
    prerequisites
      .map((p, i) =>
        p.completionStatus === PrerequisiteCompletionStatus.Automatic ? i : -1,
      )
      .filter((i) => i !== -1),
  );

  const manualItems = items.filter((_, i) => !autoCompletedIndices.has(i));
  const areAllCompleted = manualItems.every((item) => item.isCompleted);

  const handleSelectAll = () => {
    for (let i = 0; i < items.length; i++) {
      if (!autoCompletedIndices.has(i)) {
        setValue(`items.${i}.isCompleted`, !areAllCompleted, {
          shouldDirty: true,
        });
      }
    }
  };

  return (
    <PrimaryPaper component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <PrerequisitesStatusHeader
            completedCount={completedCount}
            totalCount={totalCount}
          />
          <Button variant="outlined" size="small" onClick={handleSelectAll}>
            {t(areAllCompleted ? RESOURCES.DeselectAll : RESOURCES.SelectAll)}
          </Button>
        </Box>

        {prerequisites.map(({ prerequisiteType, completionStatus }, index) => {
          const isAutoCompleted =
            completionStatus === PrerequisiteCompletionStatus.Automatic;

          return (
            <Box key={prerequisiteType}>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckboxInput
                  control={control}
                  name={`items.${index}.isCompleted`}
                  label={t(RESOURCES.Items[prerequisiteType])}
                  disabled={isAutoCompleted}
                />
                {isAutoCompleted && (
                  <Chip
                    label={t(RESOURCES.AutoVerified)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box ml={8} mt={0.5}>
                <FormInput
                  control={control}
                  name={`items.${index}.note`}
                  aria-label={t(RESOURCES.Note)}
                  placeholder={t(RESOURCES.NotePlaceholder)}
                  size="small"
                  formControlClass="fullWidth"
                  disablePadding
                  disabled={isAutoCompleted}
                />
              </Box>
            </Box>
          );
        })}

        <PrimaryButton
          fullWidth
          type="submit"
          disabled={!isDirty}
          isLoading={isPending}
          variant="contained"
          color="primary"
        >
          {t(Resources.Common.Save)}
        </PrimaryButton>
      </Stack>
    </PrimaryPaper>
  );
};
